const u2NarrativeSections = [
  {
    heading: 'Bigger Than the Band',
    paragraphs: [
      'By 2023, AWS re:Invent wasn\'t new to us anymore.',
      'We knew the routine.',
      'Las Vegas. Long conference days. Sessions. Walking. More walking. Restaurants. Conversations.',
      'And somewhere between all of it, finding something worth doing together that had absolutely nothing to do with the conference.',
      'The year before, that had meant climbing onto ATVs and heading into the Mojave Desert.',
      'In 2023, we went in a completely different direction.',
      'We went to see U2.',
      'But calling it a U2 concert doesn\'t really explain what we were going to see.',
      'Sphere had opened in Las Vegas only a few months earlier. Even from outside, the building had already become impossible to ignore. Its enormous exterior display could transform the entire structure into seemingly anything someone could imagine.',
      'Inside was something even harder to comprehend.',
      'U2 had been chosen to open the venue with U2:UV Achtung Baby Live at Sphere, a residency designed around the building rather than simply performed inside it.',
      'That distinction mattered.',
      'We had all been to concerts before.',
      'None of us had been to this.',
    ],
  },
  {
    heading: 'They Were Still Working Out the Kinks',
    paragraphs: [
      'Sphere may have represented the future of entertainment.',
      'Getting into it still needed some work.',
      'The line started inside the Venetian and just kept going.',
      'And going.',
      'At one point, it backed all the way into the casino.',
      'From there, we were funneled through a series of long corridors connecting the Venetian toward Sphere.',
      'The strange part was that we knew those corridors.',
      'Just the day before, they had been part of our daily AWS re:Invent commute, moving people between conference venues at the Venetian and Wynn.',
      'Now AWS was over.',
      'As we stood in line for U2, crews were actively tearing down the conference around us.',
      'AWS banners were coming down. Wayfinding signs were disappearing. Stanchions, displays, and other pieces of the conference buildout were being dismantled and carted away.',
      'It created this strange transition between two completely different versions of Las Vegas.',
      'Yesterday, thousands of people wearing AWS badges had been moving through these corridors trying to get to their next session.',
      'Tonight, thousands of U2 fans were standing in essentially the same space trying to get into the newest entertainment venue in the world.',
      'And it wasn\'t going particularly well.',
      'The line crawled.',
      'There seemed to be bottlenecks everywhere.',
      'Las Vegas is usually remarkably good at moving huge numbers of people. Conventions empty into casinos. Arenas fill and clear. Tens of thousands of people move between hotels, restaurants, shows, and conference venues every night without you giving much thought to the machinery behind it.',
      'This felt surprisingly improvised.',
      'Sphere may have looked like the future.',
      'Getting into it still felt very much like version 1.0.',
    ],
  },
  {
    heading: 'Finally Inside',
    paragraphs: [
      'Eventually, the line delivered us to the ticket scanners.',
      'All of our tickets were on my phone, so we gathered together while I pulled them up one by one and scanned everyone through.',
      'Finally, we were inside.',
      'On the lower level.',
      'There was just one problem.',
      'Our seats were almost all the way at the top.',
      'Section 405. Row 17.',
      'We found the escalators and started climbing.',
      'And climbing.',
      'Sphere has a series of long escalators carrying you from level to level, and getting to Section 405 meant riding them nearly all the way to the top of the building.',
      'After spending so much time slowly moving horizontally through the Venetian, now we were moving vertically through Sphere.',
      'Up another escalator.',
      'Then another.',
      'Eventually, we reached the 400 level and found Section 405.',
      'Then we had to go down.',
    ],
  },
  {
    heading: 'Section 405, Row 17',
    paragraphs: [
      'We had ridden escalators nearly all the way to the top of Sphere, but getting to our seats meant turning around and walking down into the seating bowl.',
      'That was when you really noticed how steep it was.',
      'The rows seemed to drop away beneath us.',
      'Walking down the steps toward Row 17, you couldn\'t help but look past the seats in front of you toward the floor and stage far below.',
      'It felt less like walking down the aisle of an arena and more like descending the side of something.',
      'You watched your footing.',
      'There was no casually bounding down the steps while looking around the room. The pitch was steep enough that you paid attention to where you were putting your feet.',
      'We were high.',
      'Really high.',
      'But once we made it to Row 17 and turned around, something else became obvious.',
      'These weren\'t necessarily bad seats.',
      'In a normal arena, sitting this high might mean spending the night watching the performers on a video board.',
      'Sphere was different.',
      'From Section 405, Row 17, we could see the stage far below us.',
      'More importantly, we could see almost everything else.',
    ],
  },
  {
    heading: 'The Ceiling That Wasn\'t There',
    paragraphs: [
      'Once we settled into our seats, we finally had a chance to stop worrying about where we were stepping and actually look around.',
      'Or, more accurately, look up.',
      'Above us appeared to be an enormous architectural ceiling with an oculus opening to the sky.',
      'It looked real.',
      'Not like an image of a ceiling projected onto a screen.',
      'Not like a giant video board.',
      'It looked as though we were sitting inside a massive enclosed structure with a real opening at the very top.',
      'You could even see birds flying through the sky beyond the oculus.',
      'For a moment, your brain accepted it.',
      'You knew there was a screen up there. You knew what Sphere was. You knew, logically, that what you were looking at wasn\'t the actual ceiling.',
      'But your eyes were telling you something different.',
      'That was our first real introduction to what Sphere could do.',
      'We hadn\'t heard a note from U2 yet.',
      'The show hadn\'t even started.',
      'And the building was already messing with our sense of what was real.',
    ],
  },
  {
    heading: 'And Then We Waited',
    paragraphs: [
      'And waited.',
      'Our tickets said 8:00 p.m.',
      'That time mattered to us more than it probably did to most of the people around us.',
      'Most of us had a red-eye flight home that night.',
      '12:58 a.m.',
      'When we planned the night, the math seemed easy.',
      'An 8:00 show. Finish a little before 10:00. Get out of Sphere, swing by the Cosmopolitan to pick up our luggage, and head to the airport.',
      'Nearly three hours.',
      'Plenty of time.',
      'Except 8:00 came and went.',
      'We sat beneath that impossibly realistic virtual ceiling and waited.',
      'And waited.',
      'It seemed like an eternity.',
      'U2 didn\'t take the stage until close to 9:00.',
      'Our comfortable cushion had just lost nearly an hour.',
      'Then the show started.',
      'And almost instantly, we stopped caring.',
      'At least for a while.',
    ],
  },
  {
    heading: 'Sensory Overload',
    paragraphs: [
      'There really isn\'t a better way to describe those first few minutes.',
      'It was sensory overload.',
      'The sound was extraordinarily clear.',
      'Not simply loud, which you expect at a concert, but precise.',
      'Sitting all the way up in Section 405, we weren\'t hearing the muddy wall of sound you sometimes get in a huge arena. Individual sounds seemed to have a place in the room.',
      'Then there were the visuals.',
      'Crazy doesn\'t really cover it.',
      'The screen we had been staring at before the show suddenly stopped feeling like a screen.',
      'The entire interior of Sphere became part of the performance.',
      'Sometimes the visuals were enormous and overwhelming.',
      'Sometimes they created depth where you knew there wasn\'t any.',
      'Sometimes the room appeared to move even though you knew perfectly well that it wasn\'t.',
      'Your eyes told you one thing.',
      'Your brain told you another.',
      'And U2 was playing in the middle of all of it.',
      'There were moments when you didn\'t know where to look.',
      'At Bono and the band on the stage far below us?',
      'Straight ahead?',
      'Above us?',
      'All the way toward the top of the dome?',
      'From Section 405, Row 17, we could take in almost everything.',
      'The seats that had seemed ridiculously high when we first walked down those steep steps suddenly made sense.',
      'We weren\'t just watching a concert on a stage.',
      'We were sitting inside the show.',
    ],
  },
  {
    heading: 'When Sphere Became Part of the Show',
    paragraphs: [
      'Not every song tried to overwhelm you.',
      'There were stretches when Sphere became something closer to a traditional concert venue. The focus returned to the stage, the band, and the music.',
      'After some of the enormous visual sequences, those moments almost gave your senses a chance to reset.',
      'But then there were the songs where it was obvious how much time had gone into designing something specifically for Sphere.',
      'Those were the moments when you began to understand what the building could actually do.',
    ],
  },
  {
    heading: 'Zoo Station',
    paragraphs: [
      'The very first song immediately began transforming the space around us.',
      'After staring at that convincing virtual ceiling while we waited, suddenly the room itself seemed to come alive.',
      'It was the transition from sitting inside Sphere to understanding why Sphere existed.',
    ],
  },
  {
    heading: 'The Fly',
    paragraphs: [
      '"The Fly" went in a completely different direction.',
      'Massive columns of words, symbols, and neon typography cascaded across the screen, surrounding the audience with information and imagery.',
      'From our seats high in Section 405, we could see the scale of it.',
      'There was no single screen to watch.',
      'It was everywhere.',
    ],
  },
  {
    heading: 'Atomic City',
    paragraphs: [
      'Then there was "Atomic City."',
      'Las Vegas itself became part of the show.',
      'The Vegas skyline surrounded us and then began breaking apart and deconstructing across the interior of Sphere.',
      'We were sitting in Las Vegas watching a digital Las Vegas seemingly come apart around us.',
    ],
  },
  {
    heading: 'Where the Streets Have No Name',
    paragraphs: [
      'And then came "Where the Streets Have No Name."',
      'Instead of filling the room with typography and graphics, Sphere seemed to open up.',
      'Sweeping outdoor landscapes, changing skies, stars, light, and seemingly endless horizons stretched across the dome.',
      'You knew we were still sitting inside a building in Las Vegas.',
      'Your eyes weren\'t nearly as convinced.',
      'Those were the moments when Sphere stopped being a really big screen behind a really famous band.',
      'It became the environment.',
    ],
  },
  {
    heading: 'The Set',
    paragraphs: [
      'Trying to describe every song would almost miss the point.',
      'This wasn\'t memorable because of a sequence of individual performances.',
      'It was the combination of U2, the sound, the visuals, the scale, and this completely new building surrounding all of it.',
      'So rather than a play-by-play, the set list can tell that part of the story.',
    ],
  },
  {
    heading: 'Only Scratching the Surface',
    paragraphs: [
      'Maybe the craziest realization came as the night went on.',
      'U2 was only scratching the surface.',
      'For some songs, Sphere was essentially in concert mode.',
      'U2 was on stage. The music was the focus. The enormous technology surrounding us took a supporting role.',
      'But when they unleashed it, you could see the possibilities.',
      '"The Fly" didn\'t look like "Atomic City."',
      '"Atomic City" didn\'t look like "Where the Streets Have No Name."',
      'They weren\'t simply putting different videos onto the same enormous screen.',
      'They were experimenting with entirely different ways of using the room.',
      'And U2 was first.',
      'Sphere had only been open for a couple of months. This was the first residency and one of the first attempts to figure out what an artist could actually do with a canvas this large.',
      'Sitting there, it felt less like we were seeing the ultimate Sphere concert and more like we were watching someone demonstrate what was possible.',
      'If this was version 1.0, what was this place going to look like once artists had years to figure it out?',
      'Unfortunately, as the show went on, I was also starting to think about something much less exciting.',
      'The clock.',
    ],
  },
  {
    heading: 'We Have a Plane to Catch',
    paragraphs: [
      'The show had started almost an hour later than we expected.',
      'And it kept going.',
      'As we got closer to 11:00, I started doing the math again.',
      'We still had to get out of Sphere.',
      'We still had to get back to the Cosmopolitan.',
      'All of our luggage was there.',
      'Then we had to get a taxi to the airport.',
      'Check our bags.',
      'Get through security.',
      'Get to the gate.',
      'And our flight was still leaving at 12:58 a.m.',
      'The problem was that U2 had saved some of their most famous songs for the end.',
      'This was not exactly the ideal moment to leave.',
      'Especially for Al.',
    ],
  },
  {
    heading: 'The Look',
    paragraphs: [
      'Al was the biggest U2 fan in the group.',
      'I told him we needed to start getting ready to bail.',
      'He looked at me like a sad puppy dog.',
      'We had waited forever to get into Sphere.',
      'We had climbed almost to the top of the building.',
      'We were watching U2 in this completely new venue.',
      'The band was finally getting to some of the songs everyone had been waiting for.',
      'And I was telling the biggest U2 fan in the group that we needed to leave.',
      'Then came "Beautiful Day."',
      'Al gave me a look.',
      'That look bought him the beginning of one more song.',
      'We stayed long enough to see "Beautiful Day" start.',
      'Then we reluctantly started climbing back up those same steep steps we had carefully walked down a few hours earlier.',
      'We made it to the top.',
      'But we still couldn\'t quite leave.',
      'So we stood at the top of the stairs and watched the rest.',
      'Then the concert ended.',
      'And the race began.',
    ],
  },
  {
    heading: 'Beautiful Day. Now Run.',
    paragraphs: [
      'Thousands of people began pouring out of Sphere at essentially the same time.',
      'We knew there was no way we were going to walk outside the Venetian and casually grab a taxi.',
      'Everyone else was trying to leave too.',
      'So we moved.',
      'Fast.',
      'Instead of trying to fight the crowd around the Venetian, we cut over toward Wynn.',
      'Through Wynn.',
      'Then through to Encore.',
      'There, we finally managed to grab a taxi.',
      'First stop: the Cosmopolitan.',
      'We got to the Cosmo, grabbed all of our luggage, and then needed another taxi.',
      'Next stop: the airport.',
      'The airport was only a few miles away.',
      'It felt much farther.',
      'The concert had just let out. The Strip was packed.',
      'Traffic barely moved.',
      'After spending the evening wishing U2 would hurry up and start, we were now sitting in the back of a taxi wishing Las Vegas traffic would move.',
      'The clock kept getting closer to midnight.',
      'And we crawled.',
    ],
  },
  {
    heading: 'Just After Midnight',
    paragraphs: [
      'We finally pulled up at the airport a few minutes after midnight.',
      'Now we really had to move.',
      'We got our luggage out and headed for the airline counter.',
      'They were about to close checked baggage for our flight.',
      'We got there just in time.',
      'Bags checked.',
      'Next problem.',
      'Security.',
      'We made it through.',
      'Then we ran for the gate.',
      'After an entire week at AWS re:Invent, followed by a night inside one of the most technologically advanced entertainment venues ever built, our final challenge in Las Vegas was decidedly low tech.',
      'Run.',
      'Find the gate.',
      'Keep running.',
      'Get on the plane.',
      'We made it.',
      'Our flight left at 12:58 a.m.',
    ],
  },
  {
    heading: 'Just Enough Time',
    paragraphs: [
      'There are a lot of things we remember about that night.',
      'The line backed into the Venetian casino while AWS was being torn down around us.',
      'Scanning everyone into Sphere from the tickets on my phone.',
      'The endless escalators.',
      'The steep walk down to Section 405, Row 17.',
      'The virtual ceiling and the birds flying through an oculus that didn\'t actually exist.',
      'Waiting forever for U2 to start.',
      'The incredible clarity of the sound.',
      'The sensory overload when Sphere finally came alive.',
      '"The Fly."',
      'Las Vegas deconstructing around us during "Atomic City."',
      'The world seemingly opening up during "Where the Streets Have No Name."',
      'The realization that, as incredible as all of it was, U2 was probably only scratching the surface of what this building would eventually be capable of.',
      'And Al looking at me like a sad puppy dog when I told him we might have to leave U2 early.',
      'We didn\'t quite leave.',
      'We stayed for the beginning of "Beautiful Day."',
      'We watched the rest from the top of the stairs.',
      'Then we ran.',
      'Out of Sphere.',
      'Across to Wynn.',
      'Through Encore.',
      'Taxi to the Cosmo.',
      'Grab the bags.',
      'Another taxi.',
      'Crawl through Las Vegas traffic.',
      'Airport just after midnight.',
      'Bags checked as the counter was about to close.',
      'Through security.',
      'Run to the gate.',
      'Onto a 12:58 a.m. red-eye home.',
      'When we planned the night, we thought we had plenty of time.',
      'We didn\'t.',
      'But we had just enough.',
    ],
  },
];

const u2SetListSections = [
  {
    heading: 'Achtung Baby (First Half)',
    items: [
      'Zoo Station',
      'The Fly',
      'Even Better Than the Real Thing',
      'Mysterious Ways',
      'One',
      'Until the End of the World',
      'Who\'s Gonna Ride Your Wild Horses',
      'Tryin\' to Throw Your Arms Around the World',
    ],
  },
  {
    heading: 'Acoustic / Mid-Show Break',
    items: [
      'All I Want Is You',
      'A Rainy Night in Soho (The Pogues cover, dedicated to Shane MacGowan)',
      'Desire',
      'Two Hearts Beat as One',
      'Sunday Bloody Sunday',
    ],
  },
  {
    heading: 'Achtung Baby (Second Half)',
    items: [
      'Acrobat',
      'So Cruel',
      'Ultraviolet (Light My Way)',
      'Love Is Blindness',
    ],
  },
  {
    heading: 'Encore',
    items: [
      'Elevation',
      'Atomic City',
      'Vertigo',
      'Where the Streets Have No Name',
      'With or Without You',
      'Beautiful Day',
    ],
  },
];

export default u2NarrativeSections.flatMap((section) => (
  section.heading === 'The Set' ? [section, ...u2SetListSections] : [section]
));
